import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";
import { Bar } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components and zoom plugin used by this chart.
ChartJS.register(
  BarController,
  BarElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  zoomPlugin
);


function TotalEnergyChart({
  title,
  data = [],
  grouping = "daily"
}) {
  const chartRef = useRef(null);


  // Validate the API data and convert it into Chart.js x/y points.
  const chartPoints = Array.isArray(data)
    ? data
        .filter((row) => {
          const date = new Date(row.date);
          const value = Number(row.value);

          return (
            !Number.isNaN(date.getTime()) &&
            date.getFullYear() >= 2016 &&
            !Number.isNaN(value)
          );
        })
        .map((row) => ({
          x: new Date(row.date).getTime(),
          y: Number(row.value)
        }))
    : [];


  // Match the chart's time scale to the selected grouping.
  const getTimeUnit = () => {
    if (grouping === "monthly") return "month";
    if (grouping === "yearly") return "year";

    return "day";
  };


  // Use green for AC charts and yellow for DC charts.
  const isDC = title.toUpperCase().includes("DC");

  const chartColour = isDC
    ? "#f9a825"
    : "#2e7d32";


  const chartData = {
    datasets: [
      {
        label: title,
        data: chartPoints,
        backgroundColor: chartColour,
        borderColor: chartColour,
        borderWidth: 1,

        // Allow bars to use more of the available horizontal space
        // while remaining responsive for large date ranges.
        barPercentage: 0.95,
        categoryPercentage: 0.9,
        maxBarThickness:
          grouping === "daily"
            ? 24
            : grouping === "monthly"
              ? 42
              : 55
      }
    ]
  };


  // Configure the chart axes, legend, tooltips and zoom behaviour.
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: getTimeUnit()
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
          maxRotation: 45,
          minRotation: 0
        },
        title: {
          display: true,
          text: "Date"
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Energy (MWh)"
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: {
          boxWidth: 14,
          boxHeight: 14,
          padding: 14
        }
      },
      tooltip: {
        mode: "index",
        intersect: false
      },

      // Allow horizontal zooming and panning across the selected date range.
      zoom: {
        limits: {
          x: {
            min: "original",
            max: "original",
            minRange:
              grouping === "yearly"
                ? 31536000000
                : grouping === "monthly"
                  ? 2592000000
                  : 86400000
          }
        },
        pan: {
          enabled: true,
          mode: "x",
          modifierKey: "shift"
        },
        zoom: {
          mode: "x",
          wheel: {
            enabled: true,
            modifierKey: "ctrl",
            speed: 0.08
          },
          pinch: {
            enabled: true
          },
          drag: {
            enabled: true,
            modifierKey: "shift",
            borderWidth: 1
          }
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>{title}</h2>

          <p>
            Hold <strong>Ctrl</strong> and scroll to zoom, or hold{" "}
            <strong>Shift</strong> and drag to zoom into a selected period.
          </p>
        </div>


        <ChartToolbar
          chartRef={chartRef}
          title={title}
        />
      </div>


      {/* Render the total energy consumption bar chart */}
      <div className="chart-canvas-container">
        <Bar
          ref={chartRef}
          data={chartData}
          options={options}
        />
      </div>
    </article>
  );
}


export default TotalEnergyChart;